/**
 * A identidade visual sai da MARCA, não do componente.
 *
 * O problema que isto resolve: os dois hosts servem o MESMO bundle, com
 * `VITE_BRAND` trocando a marca. Os tokens que de fato pintam a tela
 * (`--primary`, `--background`…) estavam hardcoded no `index.css` — e o bloco
 * `.dark` é comentado, no próprio arquivo, como "12brain identity". Repintar ali
 * repintaria o 12brain Academy junto, sem ninguém ter pedido.
 *
 * O `BrandingProvider` já injetava `--brand-accent-1..4` a partir da paleta
 * oficial, mas nada lia esses valores. Por isso o telão parecia de outro
 * produto: ele era a ÚNICA tela usando a paleta da marca, escrita à mão dentro
 * do componente. Não é que o telão tivesse identidade especial — é que o resto
 * nunca recebeu a dele.
 *
 * ⚠️ MARCA SEM IDENTIDADE AQUI NÃO RECEBE NADA. O 12brain (sem `VITE_BRAND`) é a
 * testemunha do escopo: se ele mudar de aparência, a mudança vazou.
 *
 * ⚠️ Os tokens são DERIVADOS da paleta, nunca hex escrito à mão. É o que faz
 * trocar a paleta em `brands.ts` mudar o produto inteiro — inclusive o telão.
 *
 * Spec: .planning/IDENTIDADE-E-NAVEGACAO.md (Parte B) · DESIGN.md
 */

/** `#4cc9f0` → `{ h: 194, s: 85, l: 62 }` */
function hexParaHsl(hex: string): { h: number; s: number; l: number } {
  const limpo = hex.replace('#', '');
  const n = limpo.length === 3
    ? limpo.split('').map((c) => c + c).join('')
    : limpo;
  const r = parseInt(n.slice(0, 2), 16) / 255;
  const g = parseInt(n.slice(2, 4), 16) / 255;
  const b = parseInt(n.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;

  if (d === 0) return { h: 0, s: 0, l: Math.round(l * 100) };

  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;

  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

/** O formato que o shadcn espera: `"194 85% 62%"`, sem `hsl()`. */
const tripla = ({ h, s, l }: { h: number; s: number; l: number }) => `${h} ${s}% ${l}%`;

/** Clareia/escurece mantendo matiz e saturação — as superfícies são a MESMA cor. */
const comLuz = (c: { h: number; s: number; l: number }, l: number) => ({ ...c, l });

/** Dessatura sem trocar de cor: cinza que ainda pertence à paleta. */
const comSat = (c: { h: number; s: number; l: number }, s: number) => ({ ...c, s });

/**
 * Monta os tokens de tema a partir dos quatro acentos da marca.
 *
 * Ordem da paleta (ver `brands.ts`): [principal, secundária, alerta, fundo].
 * Para a Globall: azul elétrico, roxo, vermelho, navy.
 */
export function tokensDaPaleta(
  paleta: readonly [string, string, string, string],
): Record<string, string> {
  const [principal, secundaria, alerta, fundo] = paleta.map(hexParaHsl) as [
    ReturnType<typeof hexParaHsl>, ReturnType<typeof hexParaHsl>,
    ReturnType<typeof hexParaHsl>, ReturnType<typeof hexParaHsl>,
  ];

  /* O texto NÃO é branco puro: um neutro puxado pro matiz do fundo pertence à
     paleta, e branco absoluto sobre navy vibra. Mesma ideia dos cinzas abaixo. */
  const texto = { h: fundo.h, s: 30, l: 96 };
  const textoFraco = { h: fundo.h, s: 18, l: 72 };

  return {
    '--background': tripla(fundo),
    '--foreground': tripla(texto),

    /* Superfícies são o MESMO navy, só mais claras. Trocar o matiz aqui é o que
       faz um app parecer montado com peças de origens diferentes. */
    '--card': tripla(comLuz(fundo, fundo.l + 5)),
    '--card-foreground': tripla(texto),
    '--popover': tripla(comLuz(fundo, fundo.l + 4)),
    '--popover-foreground': tripla(texto),

    '--primary': tripla(principal),
    /* Texto sobre o botão principal é o NAVY, não branco: o azul elétrico é
       claro demais pra carregar texto branco com o contraste que o DESIGN.md
       pede. É a mesma regra da tinta do adesivo. */
    '--primary-foreground': tripla(comLuz(fundo, 8)),

    '--secondary': tripla(comLuz(fundo, fundo.l + 12)),
    '--secondary-foreground': tripla(texto),

    '--accent': tripla(secundaria),
    '--accent-foreground': tripla(comLuz(fundo, 8)),

    '--destructive': tripla(alerta),
    '--destructive-foreground': tripla({ h: alerta.h, s: 20, l: 98 }),

    '--muted': tripla(comLuz(fundo, fundo.l + 10)),
    '--muted-foreground': tripla(textoFraco),

    '--border': tripla(comSat(comLuz(fundo, fundo.l + 16), 25)),
    '--input': tripla(comSat(comLuz(fundo, fundo.l + 16), 25)),
    '--ring': tripla(principal),

    /* O MENU LATERAL (12/08). O `ui/sidebar.tsx` do shadcn lê tokens próprios
       `--sidebar-*`, que no `index.css` são os do 12brain — teal `172 66% 50%`.
       Sem derivar aqui, a Globall abriria com o menu teal encostado num app
       azul elétrico: a peça mais visível da tela seria a única fora da paleta.

       O menu é uma superfície RECUADA, não elevada: ele fica atrás da tarefa.
       Por isso é mais ESCURO que o `--card`, e não mais claro — o cartão sobe
       na direção do olho, o menu desce. */
    '--sidebar-background': tripla(comLuz(fundo, fundo.l + 2)),
    '--sidebar-foreground': tripla(texto),
    '--sidebar-primary': tripla(principal),
    '--sidebar-primary-foreground': tripla(comLuz(fundo, 8)),
    /* Item sob o cursor: um degrau de luz, sem cor saturada. Estado inativo
       nunca recebe cor (DESIGN.md, estratégia Restrained). */
    '--sidebar-accent': tripla(comLuz(fundo, fundo.l + 9)),
    '--sidebar-accent-foreground': tripla(texto),
    '--sidebar-border': tripla(comSat(comLuz(fundo, fundo.l + 13), 25)),
    '--sidebar-ring': tripla(principal),
  };
}

/**
 * A TIPOGRAFIA DA MARCA.
 *
 * Direção vinda da arte oficial (`~/Code/ai-video/globall/brand/brand-notes.md`):
 * "NEXTGEN em cromado azul/roxo, estilo gamer/esportivo" e "tipografia bold",
 * pra um público de 14–17 anos. Isso pede display ATLÉTICA E LARGA, não uma
 * grotesca neutra.
 *
 * - **Archivo** no eixo largo (`wdth` 62–125) — energia de camisa de time, e
 *   largura é o que sustenta leitura a 5 metros no telão.
 * - **Barlow** no corpo — grotesca de sinalização, ESTREITA em relação à
 *   display, então o par tem contraste de largura de verdade; e aguenta corpo
 *   pequeno no celular do aluno.
 *
 * Nenhuma das duas está na lista de faces genéricas do `impeccable` (Inter,
 * Roboto, Fraunces, Geist, Plus Jakarta Sans, Space Grotesk) — e as duas
 * primeiras da lista são justamente as que o 12brain usa.
 *
 * ⚠️ `.font-display` do `index.css` é comentado no arquivo como "12brain brand
 * signature". Por isso a fonte entra por VARIÁVEL com fallback: marca sem
 * identidade continua em Inter/Space Grotesk, sem uma linha mudar.
 */
export const TIPOGRAFIA: Record<string, Record<string, string>> = {
  globall: {
    '--fonte-corpo': "'Barlow', system-ui, -apple-system, sans-serif",
    '--fonte-display': "'Archivo Variable', 'Barlow', system-ui, sans-serif",
    /* O eixo de largura é o que dá a cara esportiva. 112% é largo o bastante
       pra ler como jersey e estreito o bastante pra o título não quebrar em
       duas linhas no celular. */
    '--display-largura': '112%',
  },
};

/**
 * A ARTE da marca — logo e ícones.
 *
 * `/logo.png` (o logo do 12brain Academy) estava FIXO no `AuthPage`, então o
 * host da Globall abria com a marca de outro produto. Favicon, apple-touch-icon
 * e og:image tinham o mesmo problema, por serem estáticos do `index.html`.
 *
 * Marca ausente aqui continua com `/logo.png` e os ícones de sempre.
 */
export const ARTE: Record<string, { logo: string; favicon: string; touchIcon: string }> = {
  globall: {
    /* NEXTGEN cromado, trocado pelo José em 12/08 (antes: `/globall-logo.png`,
       o wordmark institucional com o globo).

       ⚠️ ELE INVERTE A REGRA DA TINTA. O logo antigo era navy sobre claro e por
       isso morava dentro de um adesivo BRANCO no login — sem a superfície clara
       as letras sumiriam no fundo navy. Este é cromado com brilho e contorno de
       neon: foi desenhado PARA o escuro, e dentro do adesivo branco ele perde o
       brilho e ainda mostra a caixa do arquivo. Por isso o `AuthPage` passou a
       desenhá-lo direto sobre o navy — ver o comentário lá.

       Baixado do host do cliente e SERVIDO DAQUI, não por URL externa: em wifi
       de escola, asset por CDN é asset que não carrega (mesma razão das fontes
       auto-hospedadas na Parte B2). O fundo cinza do JPEG original foi removido
       por preenchimento a partir das bordas — recorte por cor global comeria os
       brilhos claros de dentro das letras. */
    logo: '/nextgen-logo.png',
    /* Ícones seguem sendo o globo da marca institucional: a aba do navegador é
       da ESCOLA, não do curso. Trocar exige arte nova em 16px. */
    favicon: '/globall-favicon.png',
    touchIcon: '/globall-touch-icon.png',
  },
};

/**
 * Quais marcas têm identidade própria.
 *
 * Chave = `VITE_BRAND`. Ausente aqui = **não recebe token nenhum** e continua
 * exatamente como está — que é o caso do 12brain Academy.
 */
export const MARCAS_COM_IDENTIDADE = new Set(['globall']);

/**
 * Marcas em que o aluno entra pela SALA (QR do telão), não pelo catálogo.
 *
 * Decisão do Matheus em 03/08: "o aluno não vai ter acesso ao NextGen, ali é a
 * plataforma que o professor inicia a classe". O `/aluno` é o catálogo do
 * produto assíncrono do 12brain — em inglês, e que não é o produto dele.
 */
export const ALUNO_ENTRA_PELA_SALA = new Set(['globall']);

/**
 * Marcas em que NINGUÉM cria a própria conta.
 *
 * No NEXTGEN toda conta é provisionada: staff nasce no `/admin/escola`
 * (`staff-roster`) e aluno nasce no `/admin/turmas` (`class-roster`, com e-mail
 * sintético pra menor). A aba "Criar conta" não servia a ninguém e ainda deixava
 * qualquer visitante virar usuário logado — e usuário logado **cria turma**, que
 * a RLS permite. Superfície aberta sem uso.
 *
 * ⚠️ ISTO É A INTERFACE, NÃO O SERVIDOR. O `signUp` continua existindo na API do
 * Supabase; fechar de verdade seria desligar signup no projeto, e **os dois
 * hosts compartilham o MESMO Supabase** — o 12brain Academy, que é um produto de
 * catálogo aberto, perderia o cadastro dele junto. Por isso a decisão é por
 * marca, aqui.
 */
export const MARCAS_SEM_CADASTRO_PUBLICO = new Set(['globall']);

/**
 * Marcas que navegam por MENU LATERAL em vez de cabeçalho por tela.
 *
 * O diagnóstico de 12/08 (screenshot das 27 telas em produção): não existia
 * menu. Existia um cabeçalho que mudava a cada tela — o console oferecia
 * "Turmas" e "Escola", o painel da escola oferecia "Equipe" e "Turmas", e
 * `/admin/turmas` e `/admin/escola` não ofereciam **nada** além da seta de
 * voltar. A única constante da interface era o avatar. Sem um lugar fixo que
 * responda "onde estou e para onde posso ir", a plataforma lê como um conjunto
 * de páginas em vez de um produto.
 *
 * ⚠️ POR MARCA, e não global, por decisão do José em 12/08. Os dois hosts
 * servem o MESMO bundle: mudar a navegação sem trava mudaria o 12brain Academy
 * junto — e ele é a TESTEMUNHA de escopo deste repo (`MARCAS_COM_IDENTIDADE`).
 * Marca ausente aqui continua com o cabeçalho por tela, exatamente como está.
 *
 * Vale para TODOS os papéis, inclusive o aluno (decisão do José em 12/08,
 * contra a recomendação de manter o `/minhas-aulas` sem casca): o menu do aluno
 * é o mesmo material, com os destinos dele. No celular vira gaveta — sidebar
 * fixa em 360px de largura seria a tela inteira.
 *
 * NÃO vale para `/telao` nem `/sala`: o telão é projeção pura (decisão de
 * 05/08, sem nenhum elemento clicável) e a sala é o aluno DENTRO da aula, onde
 * navegação é convite pra sair no meio.
 *
 * Spec: .planning/IDENTIDADE-E-NAVEGACAO.md (Parte B, item B3)
 */
export const MARCAS_COM_MENU_LATERAL = new Set(['globall']);

/**
 * Marcas em que o ALUNO cria a própria conta com o código da turma.
 *
 * Pedido do José (13/08). Convive com `MARCAS_SEM_CADASTRO_PUBLICO`, e a
 * diferença é o que importa: aquela tira a aba "Criar conta" genérica, que
 * deixava qualquer visitante virar usuário logado sem gate nenhum. Esta abre um
 * caminho ESTREITO no lugar — exige o código de uma turma real e a liberação de
 * alguém da escola. O cadastro fica mais difícil do que estava, não mais fácil.
 *
 * Marca ausente aqui não ganha o link no login nem a rota /criar-conta.
 */
export const MARCAS_COM_AUTOCADASTRO_DE_ALUNO = new Set(['globall']);
